import type { RootState } from './store'
import { useState, useCallback } from 'react';
import { fetchGames } from './features/player/gameSlice'
import { fetchGameStatus } from './features/player/gameStatusSlice'
import Main from './Main';
import PlayerHome from './PlayerHome';
import LoginPage from './components/LoginPage';
import HotwashApp from './HotwashApp';
import Admin from './Admin';
import { useAppDispatch, useAppSelector } from './app/hooks'
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
  Navigate,
} from "react-router-dom";
import { isEmpty } from 'lodash';
import { IGameStatus } from './interfaces';

import { ToastNotification } from '@carbon/react';
import { Theme } from '@carbon/react';
import StackTrace from 'stacktrace-js';

const ProtectedRoute = ({ user, redirectPath = '/login' }: { user: string | null, redirectPath?: string }) => {
  if (!user) {
    return <Navigate to={redirectPath} replace />;
  }

  return <Outlet />;
};

let retryCount = 0;

const App = () => {
  const { accessToken } = useAppSelector((state: RootState) => state.auth)
  const [intervalHandle, setIntervalHandle] = useState<number>(NaN)
  const [connectionError, setConnectionError] = useState<string>('');
  const dispatch: any = useAppDispatch();

  window.addEventListener('error', async (event) => {
    const errorMessage = event.error.message;
    const st = await StackTrace.fromError(event.error);
  
    // Construct the message to send to Slack
    const message = {
      text: `TXP JavaScript Error: ${errorMessage}`,
      blocks: [
        {
          type: "section",
          text: {
            type: "mrkdwn",
            text: `*JavaScript Error:* ${errorMessage}`
          }
        },
        {
          type: "section",
          text: {
            type: "mrkdwn",
            text: `\`\`\`\n${st}\n\`\`\``
          }
        }
      ]
    };
  
    // Send the message to Slack
    fetch('https://hooks.slack.com/services/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', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(message)
    });
  });

  const intervalHandler = useCallback((command: string, gameId: string, inGame: boolean) => {
    if (command === "start") {
      setConnectionError('');
      setIntervalHandle((prevHandle) => {
        if (!isNaN(prevHandle)) {
          clearInterval(prevHandle);
        }
        const newHandle = window.setInterval(() => {
          if (!inGame) {
            dispatch(fetchGames()).then((
              (resp: {error: any, payload: IGameStatus[]}) => {
                const gameList = resp.payload;
                // Stop constantly pinging the server on error
                if (resp.error) {
                  clearInterval(newHandle);
                  setConnectionError('Unexpected error contacting server, please wait and reload the page to try again.');
                  return;
                }

                // Handle a specific error case
                if (!Array.isArray(gameList) || (gameList.length === 1 && gameList[0].error)) {
                  clearInterval(newHandle);
                  if (gameList[0].errorCode === 429) {
                    retryCount++;
                    let timeLeft = Math.min(3 * retryCount, 30);
                    setConnectionError(`Server overloaded, retrying in ${timeLeft} seconds`);
                    const noticeTimer = setInterval(() => {
                      timeLeft = timeLeft - 1;
                      setConnectionError(`Server overloaded, retrying in ${timeLeft} seconds`);
                      if (timeLeft < 1) {
                        clearInterval(noticeTimer);
                      }
                    }, 1000)

                    setTimeout(() => {
                      intervalHandler('start', gameId, inGame);
                    }, timeLeft * 1000)
                    return;
                  }
                  else {
                    setConnectionError('Unexpected error contacting server, please wait and reload the page to try again.');
                    return;
                  }
                }
                retryCount = 0;
            }))
          }

          if (!isEmpty(gameId)) {
              dispatch(fetchGameStatus(gameId)).then(
                (resp: {error: any, payload: IGameStatus[]}) => {
                  const gameList = resp.payload;
                  // Stop constantly pinging the server on error
                  if (resp.error) {
                    clearInterval(newHandle);
                    setConnectionError('Unexpected error contacting server, please wait and reload the page to try again.');
                    return;
                  }

                  // Handle a specific error case
                  if (!Array.isArray(gameList) || (gameList.length === 1 && gameList[0].error)) {
                    clearInterval(newHandle);
                    if (gameList[0].errorCode === 429) {
                      retryCount++;
                      let timeLeft = Math.min(3 * retryCount, 30);
                      setConnectionError(`Server overloaded, retrying in ${timeLeft} seconds`);
                      const noticeTimer = setInterval(() => {
                        timeLeft = timeLeft - 1;
                        setConnectionError(`Server overloaded, retrying in ${timeLeft} seconds`);
                        if (timeLeft < 1) {
                          clearInterval(noticeTimer);
                        }
                      }, 1000)

                      setTimeout(() => {
                        intervalHandler('start', gameId, inGame);
                      }, timeLeft * 1000)
                      return;
                    }
                    else if(gameList[0].errorCode === 404) { // game is not found, likely may have been deleted by admin or host.
                      window.location.replace('/player')
                      // TODO: show a notification to the player. The code below will not work correctly.
                      //navigate('/player')
                      //setConnectionError('Game no longer found, it was likely removed by the host or an admin.')
                      return;
                    }
                    else {
                      setConnectionError('Unexpected error contacting server, please wait and reload the page to try again.')
                      return;
                    }
                  }
                }
              )
          }
        }, 3000)
        return newHandle;
      });
    }

    if (command === "stop") {
        clearInterval(intervalHandle)
        setIntervalHandle(NaN);
    }
  }, [intervalHandle, setIntervalHandle]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Router>
      <Routes>
        <Route path="/" element={<Navigate to="/player" />} />
        <Route element={<ProtectedRoute user={accessToken} />}>
          <Route path="/scenariocreator" element={<Main intervalHandler={intervalHandler} />} />
          <Route path="/player" element={<PlayerHome intervalHandler={intervalHandler} />} />
          <Route path="/hotwash" element={<HotwashApp intervalHandler={intervalHandler} />} />
          <Route path="/admin" element={<Admin test="test" />} />
        </Route>
        <Route path="/login" element={<LoginPage intervalHandler={intervalHandler} />} />
      </Routes>
      {
        !isEmpty(connectionError) &&
        <Theme theme="g100">{
            <ToastNotification
              aria-label="Connection Error"
              kind="error"
              onCloseButtonClick={function noRefCheck(){}}
              role="status"
              statusIconDescription="notification"
              subtitle={connectionError}
              timeout={0}
              title="Connection Issue"
            />
        }</Theme>
      }
    </Router>
  );
}

export default App;